import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Alert,
    Badge,
    Button,
    Card,
    Form,
    Modal,
    Spinner,
    Table,
} from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiMail,
    FiRefreshCw,
} from 'react-icons/fi';
import { sendStandardObservationNotTriggeredEmails } from '../../services/api';
import { formatRequestError } from '../../utils/errorHandler';
import { normalizeAdminRole } from '../../utils/ecmwfExcelImport';
import { resolveAdminRole } from '../../utils/checkForecastBulkDelete';
import { useTranslation } from '../../hooks/useTranslation';
import { useLongRunningProgress, getLongWaitMessage } from '../../hooks/useLongRunningProgress';
import LongRunningOperationBanner from './LongRunningOperationBanner';
import './StandardForecastTriggerEmailTab.css';

const PREFIX = 'pages.adminTripsDaily.standardObservationNotTriggeredEmail';

const ALLOWED_ROLES = new Set([
    'admin',
    'administrator',
    'administrateur',
    'operations_limited',
    'restricted_admin',
]);

const canRunStandardObservationNotTriggeredEmail = (role) => (
    ALLOWED_ROLES.has(normalizeAdminRole(role))
);

const parseApiError = (error, t) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    if (status === 401) {
        return {
            title: t(`${PREFIX}.errors.unauthorizedTitle`),
            message: t(`${PREFIX}.errors.unauthorized`),
        };
    }
    if (status === 403) {
        const apiMessage = detail && typeof detail === 'object' && detail.message
            ? String(detail.message)
            : (typeof detail === 'string' ? detail : null);
        return {
            title: t(`${PREFIX}.errors.forbiddenTitle`),
            message: apiMessage || t(`${PREFIX}.errors.forbidden`),
        };
    }
    if (status === 404) {
        const apiMessage = detail && typeof detail === 'object' && detail.message
            ? String(detail.message)
            : (typeof detail === 'string' ? detail : null);
        return {
            title: t(`${PREFIX}.errors.notFoundTitle`),
            message: apiMessage || t(`${PREFIX}.errors.notFound`),
        };
    }
    if (status === 500) {
        return {
            title: t(`${PREFIX}.errors.serverTitle`),
            message: t(`${PREFIX}.errors.server`),
        };
    }

    if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.message) {
        return {
            title: t(`${PREFIX}.errors.genericTitle`),
            message: String(detail.message),
        };
    }
    if (typeof detail === 'string' && detail.trim()) {
        return {
            title: t(`${PREFIX}.errors.genericTitle`),
            message: detail,
        };
    }

    return {
        title: t(`${PREFIX}.errors.genericTitle`),
        message: formatRequestError(error, t(`${PREFIX}.error`), {
            timeout: t(`${PREFIX}.errors.timeout`),
            network: t(`${PREFIX}.errors.network`),
        }),
    };
};

const EntryTable = ({ title, entries, columns, emptyLabel }) => {
    if (!Array.isArray(entries) || entries.length === 0) {
        return (
            <div className="sfte-entry">
                <h4 className="sfte-entry__title">{title}</h4>
                <p className="sfte-entry__empty mb-0">{emptyLabel}</p>
            </div>
        );
    }

    return (
        <div className="sfte-entry">
            <h4 className="sfte-entry__title">
                {title}{' '}
                <Badge bg="secondary">{entries.length}</Badge>
            </h4>
            <div className="table-responsive border rounded">
                <Table striped bordered hover size="sm" className="mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>#</th>
                            {columns.map((col) => (
                                <th key={col.key}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry, index) => {
                            const row = entry && typeof entry === 'object' ? entry : {};
                            return (
                                <tr key={`${title}-${row.policy_code || index}-${row.claim_id || index}`}>
                                    <td>{index + 1}</td>
                                    {columns.map((col) => (
                                        <td key={col.key} className={col.className || 'small'}>
                                            {col.render
                                                ? col.render(row)
                                                : (row[col.key] != null && row[col.key] !== ''
                                                    ? String(row[col.key])
                                                    : '—')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

const StandardObservationNotTriggeredEmailTab = ({
    partnerOptions = [],
    partnersLoading = false,
    partnersError = null,
}) => {
    const { t } = useTranslation();
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const canRun = canRunStandardObservationNotTriggeredEmail(resolveAdminRole(accessToken));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [partnerSearch, setPartnerSearch] = useState('');

    const filteredPartnerOptions = useMemo(() => {
        const query = partnerSearch.trim().toLowerCase();
        if (!query) return partnerOptions;
        return partnerOptions.filter((name) => name.toLowerCase().includes(query));
    }, [partnerOptions, partnerSearch]);

    const selectedCompanyName = companyName.trim();

    const progressSteps = useMemo(() => ([
        t(`${PREFIX}.progress.step1`),
        t(`${PREFIX}.progress.step2`),
        t(`${PREFIX}.progress.step3`),
    ]), [t]);

    const { currentStep, elapsedSeconds } = useLongRunningProgress(loading, progressSteps);
    const longWaitNote = getLongWaitMessage(elapsedSeconds, {
        stillNormal: t(`${PREFIX}.progress.stillNormal`),
        long: t(`${PREFIX}.progress.longWait`),
        twoMinutes: t(`${PREFIX}.progress.twoMinutesWait`),
        veryLong: t(`${PREFIX}.progress.veryLongWait`),
        overFourMinutes: t(`${PREFIX}.progress.overFourMinutesWait`),
    });

    const checkClaims = result?.check_claims && typeof result.check_claims === 'object'
        ? result.check_claims
        : {};
    const metadata = checkClaims.metadata && typeof checkClaims.metadata === 'object'
        ? checkClaims.metadata
        : {};
    const notTriggeredEmails = result?.not_triggered_emails
        && typeof result.not_triggered_emails === 'object'
        ? result.not_triggered_emails
        : {};
    const emailSummary = notTriggeredEmails.summary && typeof notTriggeredEmails.summary === 'object'
        ? notTriggeredEmails.summary
        : {};
    const sent = Array.isArray(notTriggeredEmails.sent) ? notTriggeredEmails.sent : [];
    const skipped = Array.isArray(notTriggeredEmails.skipped) ? notTriggeredEmails.skipped : [];
    const failed = Array.isArray(notTriggeredEmails.failed) ? notTriggeredEmails.failed : [];
    const isSuccess = result?.status === 'success' || result?.status_code === 200;
    const checkInfosCount = Array.isArray(checkClaims.check_infos)
        ? checkClaims.check_infos.length
        : (metadata.count ?? null);

    const openConfirm = () => {
        if (loading || !canRun) return;
        setError(null);
        setShowConfirm(true);
    };

    const runSend = async () => {
        if (loading || !canRun) return;
        setShowConfirm(false);
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const response = await sendStandardObservationNotTriggeredEmails(selectedCompanyName);
            setResult(response);
        } catch (err) {
            setError(parseApiError(err, t));
        } finally {
            setLoading(false);
        }
    };

    const sentColumns = [
        {
            key: 'policy_code',
            label: t(`${PREFIX}.colPolicy`),
            render: (row) => <code>{row.policy_code || '—'}</code>,
        },
        { key: 'claim_id', label: t(`${PREFIX}.colClaim`) },
        {
            key: 'recipients',
            label: t(`${PREFIX}.colRecipients`),
            render: (row) => {
                const recipients = row.recipients;
                if (Array.isArray(recipients)) return recipients.join(', ') || '—';
                return recipients != null ? String(recipients) : '—';
            },
        },
    ];

    const skippedColumns = [
        {
            key: 'policy_code',
            label: t(`${PREFIX}.colPolicy`),
            render: (row) => <code>{row.policy_code || '—'}</code>,
        },
        { key: 'claim_id', label: t(`${PREFIX}.colClaim`) },
        { key: 'reason', label: t(`${PREFIX}.colReason`) },
    ];

    const failedColumns = [
        {
            key: 'policy_code',
            label: t(`${PREFIX}.colPolicy`),
            render: (row) => <code>{row.policy_code || '—'}</code>,
        },
        { key: 'claim_id', label: t(`${PREFIX}.colClaim`) },
        { key: 'error', label: t(`${PREFIX}.colError`), className: 'small text-break' },
    ];

    const hasPrimaryStats = (
        checkInfosCount != null
        || emailSummary.sent_count != null
        || emailSummary.skipped_count != null
        || emailSummary.failed_count != null
    );

    return (
        <div className="standard-forecast-trigger-email-tab">
            <header className="sfte-header">
                <h2 className="sfte-title">{t(`${PREFIX}.title`)}</h2>
                <p className="sfte-subtitle">{t(`${PREFIX}.description`)}</p>
            </header>

            <Card className="sfte-filter mb-3">
                <Card.Header>
                    <h6 className="mb-0">{t('pages.adminTripsDaily.partnerFilterTitle')}</h6>
                </Card.Header>
                <Card.Body>
                    <p className="text-muted small mb-3">{t(`${PREFIX}.filterDescription`)}</p>
                    <Form.Control
                        type="text"
                        value={partnerSearch}
                        onChange={(e) => setPartnerSearch(e.target.value)}
                        placeholder={t('pages.adminTripsDaily.partnerSearch')}
                        disabled={partnersLoading || loading || !canRun}
                        className="mb-2"
                    />
                    <div className="sfte-filter__list">
                        <Form.Check
                            type="checkbox"
                            id="sonte-partner-all"
                            label={t('pages.adminTripsDaily.allPartners')}
                            checked={!selectedCompanyName}
                            disabled={partnersLoading || loading || !canRun}
                            onChange={() => setCompanyName('')}
                            className="mb-2 fw-semibold"
                        />
                        {filteredPartnerOptions.length === 0 && !partnersLoading ? (
                            <div className="text-muted small px-1 py-2">
                                {t(`${PREFIX}.noPartners`)}
                            </div>
                        ) : (
                            filteredPartnerOptions.map((name, index) => (
                                <Form.Check
                                    key={`sonte-partner-${name}-${index}`}
                                    type="checkbox"
                                    id={`sonte-partner-${name}-${index}`}
                                    label={name}
                                    checked={selectedCompanyName === name}
                                    disabled={partnersLoading || loading || !canRun}
                                    onChange={(e) => setCompanyName(e.target.checked ? name : '')}
                                    className="mb-1"
                                />
                            ))
                        )}
                    </div>
                    {selectedCompanyName ? (
                        <div className="mt-2 d-flex flex-wrap gap-1">
                            <Badge bg="secondary">{selectedCompanyName}</Badge>
                        </div>
                    ) : null}
                    {partnersLoading && (
                        <div className="text-muted small mt-1">{t('common.loading')}</div>
                    )}
                    {partnersError && (
                        <Alert variant="danger" className="mt-2 mb-0 py-2">{partnersError}</Alert>
                    )}
                </Card.Body>
            </Card>

            <section className="sfte-action">
                <div className="sfte-action__info">
                    <div className="sfte-action__ready">{t(`${PREFIX}.readyTitle`)}</div>
                    <div className="sfte-action__scope">
                        {selectedCompanyName
                            ? t(`${PREFIX}.selectedCompany`, { companyName: selectedCompanyName })
                            : t(`${PREFIX}.allPartnersHint`)}
                    </div>
                    {!canRun && (
                        <Alert variant="warning" className="mb-0 py-2 px-3">
                            <strong className="d-block mb-1">{t(`${PREFIX}.errors.forbiddenTitle`)}</strong>
                            {t(`${PREFIX}.errors.forbidden`)}
                        </Alert>
                    )}
                </div>
                <Button
                    type="button"
                    variant="primary"
                    className="btn sfte-btn-primary"
                    onClick={openConfirm}
                    disabled={loading || !canRun || partnersLoading}
                >
                    {loading ? (
                        <>
                            <Spinner size="sm" animation="border" aria-hidden />
                            {t(`${PREFIX}.running`)}
                        </>
                    ) : (
                        <>
                            <FiMail size={16} aria-hidden />
                            {t(`${PREFIX}.runButton`)}
                        </>
                    )}
                </Button>
            </section>

            <LongRunningOperationBanner
                show={loading}
                sticky
                title={t(`${PREFIX}.progress.title`)}
                expectedDuration={t(`${PREFIX}.progress.expectedDuration`)}
                message={currentStep}
                elapsedSeconds={elapsedSeconds}
                elapsedLabel={t(`${PREFIX}.progress.elapsed`)}
                longWaitNote={longWaitNote}
                hint={t(`${PREFIX}.progress.hint`)}
            />

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="sfte-alert">
                    <div className="d-flex align-items-start gap-2 mb-2">
                        <FiAlertTriangle className="flex-shrink-0 mt-1" aria-hidden />
                        <div>
                            <strong className="d-block mb-1">{error.title}</strong>
                            <span>{error.message}</span>
                        </div>
                    </div>
                    {canRun && (
                        <div className="text-end">
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={runSend}
                                disabled={loading}
                            >
                                <FiRefreshCw size={14} className="me-1" aria-hidden />
                                {t(`${PREFIX}.retry`)}
                            </Button>
                        </div>
                    )}
                </Alert>
            )}

            {result && (
                <section className={`sfte-result ${isSuccess ? 'is-success' : 'is-warning'}`}>
                    <div className="sfte-result__header">
                        {isSuccess
                            ? <FiCheckCircle className="sfte-result__icon" aria-hidden />
                            : <FiMail className="sfte-result__icon" aria-hidden />}
                        <div>
                            <h3 className="sfte-result__title">{t(`${PREFIX}.resultTitle`)}</h3>
                            <p className="sfte-result__message mb-0">
                                {result.message || t(`${PREFIX}.successDefault`)}
                            </p>
                        </div>
                        {result.status && (
                            <Badge bg={isSuccess ? 'success' : 'secondary'} className="sfte-result__status">
                                {result.status}
                            </Badge>
                        )}
                    </div>

                    {hasPrimaryStats && (
                        <div className="sfte-stats">
                            {checkInfosCount != null && (
                                <div className="sfte-stat">
                                    <div className="sfte-stat__label">{t(`${PREFIX}.checkInfosCount`)}</div>
                                    <div className="sfte-stat__value">{checkInfosCount}</div>
                                </div>
                            )}
                            {emailSummary.sent_count != null && (
                                <div className="sfte-stat">
                                    <div className="sfte-stat__label">{t(`${PREFIX}.sentCount`)}</div>
                                    <div className="sfte-stat__value is-success">{emailSummary.sent_count}</div>
                                </div>
                            )}
                            {emailSummary.skipped_count != null && (
                                <div className="sfte-stat">
                                    <div className="sfte-stat__label">{t(`${PREFIX}.skippedCount`)}</div>
                                    <div className="sfte-stat__value">{emailSummary.skipped_count}</div>
                                </div>
                            )}
                            {emailSummary.failed_count != null && (
                                <div className="sfte-stat">
                                    <div className="sfte-stat__label">{t(`${PREFIX}.failedCount`)}</div>
                                    <div className="sfte-stat__value is-danger">{emailSummary.failed_count}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {(result.timestamp || metadata.forecast_hour || metadata.observation_hour || metadata.processing_time_ms != null) && (
                        <div className="sfte-result__meta">
                            {result.timestamp && (
                                <span>
                                    {t(`${PREFIX}.timestamp`)} : {result.timestamp}
                                </span>
                            )}
                            {metadata.forecast_hour && (
                                <span>
                                    {t(`${PREFIX}.forecastHour`)} : {metadata.forecast_hour}
                                </span>
                            )}
                            {metadata.observation_hour && (
                                <span>
                                    {t(`${PREFIX}.observationHour`)} : {metadata.observation_hour}
                                </span>
                            )}
                            {metadata.processing_time_ms != null && (
                                <span>
                                    {t(`${PREFIX}.processingTime`)} :{' '}
                                    {Math.round(metadata.processing_time_ms)} ms
                                </span>
                            )}
                        </div>
                    )}

                    <EntryTable
                        title={t(`${PREFIX}.sentTitle`)}
                        entries={sent}
                        columns={sentColumns}
                        emptyLabel={t(`${PREFIX}.emptyList`)}
                    />
                    <EntryTable
                        title={t(`${PREFIX}.skippedTitle`)}
                        entries={skipped}
                        columns={skippedColumns}
                        emptyLabel={t(`${PREFIX}.emptyList`)}
                    />
                    <EntryTable
                        title={t(`${PREFIX}.failedTitle`)}
                        entries={failed}
                        columns={failedColumns}
                        emptyLabel={t(`${PREFIX}.emptyList`)}
                    />

                    <div className="sfte-result__footer">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setResult(null)}
                        >
                            {t(`${PREFIX}.clearResult`)}
                        </Button>
                    </div>
                </section>
            )}

            <Modal
                show={showConfirm}
                onHide={() => !loading && setShowConfirm(false)}
                centered
                className="sfte-confirm-modal"
            >
                <Modal.Header closeButton={!loading}>
                    <Modal.Title>
                        <FiMail className="me-2" aria-hidden />
                        {t(`${PREFIX}.confirmTitle`)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">{t(`${PREFIX}.confirmMessage`)}</p>
                    <p className="mb-0 text-muted small">
                        {selectedCompanyName
                            ? t(`${PREFIX}.confirmFilterCompany`, { companyName: selectedCompanyName })
                            : t(`${PREFIX}.confirmFilterAll`)}
                    </p>
                </Modal.Body>
                <Modal.Footer className="sfte-confirm-modal__footer">
                    <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={() => setShowConfirm(false)}
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        className="btn sfte-btn-primary"
                        onClick={runSend}
                        disabled={loading || !canRun}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" animation="border" aria-hidden />
                                {t(`${PREFIX}.running`)}
                            </>
                        ) : (
                            <>
                                <FiMail size={16} aria-hidden />
                                {t(`${PREFIX}.confirmRun`)}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StandardObservationNotTriggeredEmailTab;
